/**
 * Map Store Helpers
 *
 * Map-specific utility functions (viewport center, zoom).
 */

// Re-export location types for backward compatibility
// TODO: Remove these re-exports in Phase 4
export type { SearchPoint, MapBounds } from '@/features/locations/stores/helpers';
export {
  SEARCH_POINT_STORAGE_KEY,
  toRadians,
  calculateDistance,
  loadSearchPointFromStorage,
  saveSearchPointToStorage,
} from '@/features/locations/stores/helpers';

/**
 * LocalStorage key for persisting map center
 */
export const MAP_CENTER_STORAGE_KEY = 'peach-truck-map-center';

/**
 * Map center/bounds for geocoding search results
 */
export interface MapCenter {
  lat: number;
  lng: number;
  zoom?: number;
}

/**
 * Load map center from localStorage
 *
 * @returns MapCenter object or null if not found or invalid
 */
export function loadMapCenterFromStorage(): MapCenter | null {
  try {
    // Guard against missing localStorage
    if (typeof localStorage === 'undefined') return null;

    const stored = localStorage.getItem(MAP_CENTER_STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as MapCenter;

    // Validate structure
    if (
      typeof parsed.lat !== 'number' ||
      typeof parsed.lng !== 'number' ||
      isNaN(parsed.lat) ||
      isNaN(parsed.lng)
    ) {
      console.warn('[MapStore] Invalid map center in localStorage, clearing');
      localStorage.removeItem(MAP_CENTER_STORAGE_KEY);
      return null;
    }

    // Validate optional zoom
    if (parsed.zoom !== undefined) {
      if (typeof parsed.zoom !== 'number' || isNaN(parsed.zoom)) {
        console.warn('[MapStore] Invalid zoom in localStorage, clearing');
        delete parsed.zoom;
      }
    }

    return parsed;
  } catch (error) {
    console.error('[MapStore] Error loading map center from localStorage:', error);
    return null;
  }
}

/**
 * Save map center to localStorage
 *
 * @param center - MapCenter to save (null to clear)
 */
export function saveMapCenterToStorage(center: MapCenter | null): void {
  try {
    // Guard against missing localStorage
    if (typeof localStorage === 'undefined') return;

    if (center === null) {
      localStorage.removeItem(MAP_CENTER_STORAGE_KEY);
    } else {
      localStorage.setItem(MAP_CENTER_STORAGE_KEY, JSON.stringify(center));
    }
  } catch (error) {
    console.warn('[MapStore] Error saving map center to localStorage:', error);
  }
}
